import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import React from 'react';
import { GameService } from '../services/gameService';
import { colors } from '../constants/colors';
import { useMobileDetection } from '../hooks/useMobileDetection';

function CreateGame() {
  const [hostName, setHostName] = useState('');
  const [gameSettings, setGameSettings] = useState({
    maxPlayers: 10,
    timePerQuestion: 120,
    totalQuestions: 10
  });
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const gameService = new GameService();
  const { isMobile } = useMobileDetection();

  const generateGameCode = () => {
    return Math.random().toString(36).substring(2, 8).toUpperCase();
  };

  const handleCreateGame = async () => {
    if (!hostName.trim()) {
      setError('Please enter your name');
      return;
    }

    if (hostName.trim().length < 2) {
      setError('Name must be at least 2 characters');
      return;
    }

    setIsCreating(true);
    setError('');

    try {
      const gameId = await gameService.createGame(hostName.trim());
      
      console.log('✅ Game created successfully with ID:', gameId);
      console.log('Host:', hostName);
      console.log('Settings:', gameSettings);
      
      navigate(`/game/${gameId}`, { 
        state: { 
          playerName: hostName.trim(), 
          isHost: true,
          gameSettings 
        } 
      });
    } catch (error) {
      console.error('❌ Error creating game:', error);
      setError('Failed to create game. Please try again.');
      setIsCreating(false);
    }
  };

  const containerStyle = {
    minHeight: '100vh',
    maxHeight: '100vh', 
    backgroundColor: colors.outerBackground,
    color: colors.textOnLight,
    padding: isMobile ? '2px' : '20px',
    fontFamily: 'Arial, sans-serif',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden'
  };

  const contentStyle = {
    maxWidth: '600px',
    width: '100%'
  };

  const backLinkStyle = {
    color: colors.textOnLight,
    textDecoration: 'none',
    marginBottom: '20px',
    display: 'inline-block',
    opacity: 0.8,
    transition: 'opacity 0.3s ease'
  };

  const titleStyle = {
    fontSize: '2.5rem',
    marginBottom: '30px',
    textAlign: 'center',
    color: colors.textOnLight
  };

  const errorStyle = {
    backgroundColor: colors.error,
    color: 'white',
    padding: '15px',
    borderRadius: '8px',
    marginBottom: '20px',
    textAlign: 'center'
  };

  const formSectionStyle = {
    background: colors.innerBackground,
    borderRadius: '15px',
    padding: '30px',
    color: colors.textOnDark,
    boxShadow: '0 8px 32px rgba(26, 33, 38, 0.3)'
  };

  const inputGroupStyle = {
    marginBottom: '20px'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '600',
    color: colors.textOnDark
  };

  const nameInputStyle = {
    width: '100%',
    padding: '12px',
    border: `2px solid ${colors.accent}`,
    borderRadius: '8px',
    fontSize: '16px',
    background: colors.inputBackground,
    color: colors.textOnLight,
    boxSizing: 'border-box'
  };

  const settingsSectionStyle = {
    margin: '30px 0'
  };

  const settingsTitleStyle = {
    marginBottom: '20px',
    textAlign: 'center',
    color: colors.textOnDark
  };

  const settingItemStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px',
    padding: '10px',
    background: colors.cardBackground,
    borderRadius: '8px',
    color: colors.textOnDark
  };

  const selectStyle = {
    padding: '8px',
    border: `1px solid ${colors.accent}`,
    borderRadius: '4px',
    background: colors.inputBackground,
    color: colors.textOnLight
  };

  const buttonStyle = {
    backgroundColor: isCreating || !hostName.trim() ? colors.buttonDisabled : colors.accent,
    color: 'white',
    border: 'none',
    padding: '16px 32px',
    fontSize: '18px',
    borderRadius: '8px',
    cursor: isCreating || !hostName.trim() ? 'not-allowed' : 'pointer',
    transition: 'all 0.3s ease',
    width: '100%',
    fontWeight: '600'
  };

  return (
    <div style={containerStyle}>
      <div style={contentStyle}>
        <Link 
          to="/" 
          style={backLinkStyle}
          onMouseEnter={(e) => e.target.style.opacity = '1'}
          onMouseLeave={(e) => e.target.style.opacity = '0.8'}
        >
          ← Back to Home
        </Link>
        
        <h1 style={titleStyle}>Create New Game</h1>
        
        {error && (
          <div style={errorStyle}>
            {error}
          </div>
        )}
        
        <div style={formSectionStyle}>
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Your Name:</label>
            <input
              type="text"
              placeholder="Enter your name"
              value={hostName}
              onChange={(e) => setHostName(e.target.value)}
              style={nameInputStyle}
              disabled={isCreating}
              maxLength="20"
              onFocus={(e) => e.target.style.boxShadow = `0 0 0 2px ${colors.accentBorder}`}
              onBlur={(e) => e.target.style.boxShadow = 'none'}
            />
          </div>
          
          <div style={settingsSectionStyle}>
            <h3 style={settingsTitleStyle}>Game Settings</h3>
            
            <div style={settingItemStyle}>
              <label>Max Players:</label>
              <select 
                value={gameSettings.maxPlayers}
                onChange={(e) => setGameSettings({
                  ...gameSettings, 
                  maxPlayers: parseInt(e.target.value)
                })}
                disabled={isCreating}
                style={selectStyle}
              >
                <option value={5}>5 Players</option>
                <option value={8}>8 Players</option>
                <option value={10}>10 Players</option>
                <option value={15}>15 Players</option>
              </select>
            </div>
            
            <div style={settingItemStyle}>
              <label>Time per Question:</label>
              <select 
                value={gameSettings.timePerQuestion}
                onChange={(e) => setGameSettings({
                  ...gameSettings, 
                  timePerQuestion: parseInt(e.target.value)
                })}
                disabled={isCreating}
                style={selectStyle}
              >
                <option value={60}>60 seconds</option>
                <option value={90}>90 seconds</option>
                <option value={120}>120 seconds</option>
                <option value={180}>180 seconds</option>
              </select>
            </div>
            
            <div style={settingItemStyle}>
              <label>Total Questions:</label>
              <select 
                value={gameSettings.totalQuestions}
                onChange={(e) => setGameSettings({
                  ...gameSettings, 
                  totalQuestions: parseInt(e.target.value)
                })}
                disabled={isCreating}
                style={selectStyle}
              >
                <option value={5}>5 Questions</option>
                <option value={10}>10 Questions</option>
                <option value={15}>15 Questions</option>
                <option value={20}>20 Questions</option>
              </select>
            </div>
          </div>
          
          <button 
            onClick={handleCreateGame}
            disabled={isCreating || !hostName.trim()}
            style={buttonStyle}
            onMouseEnter={(e) => {
              if (!isCreating && hostName.trim()) {
                e.target.style.backgroundColor = colors.accentHover;
              }
            }}
            onMouseLeave={(e) => {
              if (!isCreating && hostName.trim()) {
                e.target.style.backgroundColor = colors.accent;
              }
            }}
          >
            {isCreating ? '🔄 Creating Game...' : 'Create Game Room'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CreateGame;